import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  ArrowUpRightIcon,
  CalendarDaysIcon,
  ChevronRightIcon,
  MapPinIcon,
  MedalIcon,
  TrophyIcon,
} from "lucide-react";
import { Fragment, lazy, type ReactNode, Suspense, useCallback, useEffect, useState } from "react";
import { AppHeader } from "@/components/app/app-header";
import { ClubIdentity } from "@/components/app/club-identity";
import { PageContext } from "@/components/app/page-context";
import { ColorDot } from "@/components/app/record";
import { RosterProfileRow } from "@/components/app/roster-profile-row";
import { SectionNav } from "@/components/app/section-nav";
import { TeamChip } from "@/components/app/team-chip";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useGlassSurface } from "@/lib/ground";
import { useHeldTeams } from "@/lib/held-teams";
import {
  boardBundleQuery,
  clubsQuery,
  clubTournamentsQuery,
  tournamentProfilesQuery,
} from "@/lib/queries";
import { tournamentHref, useTournament } from "@/lib/tournament";
import { useCalendarNow } from "@/lib/use-calendar-now";
import { cn } from "@/lib/utils";
import type { PublicAward } from "../../shared/awards";
import { showsTournamentInfo } from "../../shared/board-info";
import { boardTableSections } from "../../shared/board-tables";
import { clubOfTeam } from "../../shared/club";
import { identityFrom, type Person } from "../../shared/identity";
import type { RosterProfileLink } from "../../shared/player-profile";
import {
  type Coverage,
  combinedStats,
  creditRate,
  divisionCoverage,
  inDivision,
  type PlayerStatRow,
  playerStats,
  type RankedPlayerRow,
  rankPlayers,
  STAT_KEYS,
  type StatKey,
  statRate,
  tournamentCoverage,
  withCredit,
} from "../../shared/player-stats";
import { hasCompletePodiums, tournamentPodium } from "../../shared/podium";
import { DEFAULT_SETTINGS } from "../../shared/settings";
import {
  filterSchedule,
  type ScheduleFilters,
  scheduleFilters,
  tournamentPulse,
} from "../../shared/spectator";
import type { PublicSpiritGame, PublicSpiritPayload, SpiritStandingRow } from "../../shared/spirit";
import { resolveSpiritAwards } from "../../shared/spirit-awards";
import {
  activeDivisions,
  divisionTables,
  type StandingRow,
  slotContext,
} from "../../shared/standings";
import { hasTournamentInformation } from "../../shared/tournament-information";
import { tournamentState } from "../../shared/tournament-state";
import {
  type Division,
  effectiveLabel,
  type Field,
  type FieldId,
  type GameStats,
  gameSlots,
  isBreak,
  type Player,
  type PointEvent,
  playerLabel,
  resolveSlot,
  type ScheduledGame,
  type Team,
  UNASSIGNED_DIVISION,
} from "../../shared/types";
import { useT } from "../i18n";
import { dateRangeOf, dayHeading } from "../i18n/format";
import { divisionNameOf, divisionTagOf, fieldNameOf, gameNoteOf, statLabel } from "../i18n/labels";
import { type LiveGame, useGameState } from "../lib/useGameState";
import { Awards, resolveAwards } from "./board/Awards";
import { GameStory } from "./board/GameStory";
import { LiveView } from "./board/LiveView";
import {
  type BoardSide,
  type LabelCtx,
  Section,
  SideLine,
  sideOf,
  Tag,
  TeamCode,
  timeOf,
} from "./board/parts";
import type { RosterLine } from "./board/TeamRoster";
import type { TeamGameRow } from "./board/TeamSheet";
import { heldTeamLabel, TeamWorkspaceShortcuts } from "./board/TeamWorkspaceShortcuts";
import { TournamentBanner } from "./board/TournamentBanner";
import { TournamentInfo } from "./board/TournamentInfo";

const MatchPreview = lazy(() =>
  import("./board/SpectatorInsights").then((m) => ({ default: m.MatchPreview })),
);
const TeamProgression = lazy(() =>
  import("./board/TeamProgression").then((m) => ({ default: m.TeamProgression })),
);
const TeamNextMatch = lazy(() =>
  import("./board/TeamProgression").then((m) => ({ default: m.TeamNextMatch })),
);
const TeamRoster = lazy(() =>
  import("./board/TeamRoster").then((m) => ({ default: m.TeamRoster })),
);
const RelatedClubHistory = lazy(() =>
  import("./profile/RelatedClubHistory").then((m) => ({ default: m.RelatedClubHistory })),
);
const ScheduleFilterBar = lazy(() =>
  import("./board/SpectatorInsights").then((m) => ({ default: m.ScheduleFilterBar })),
);
const StandoutPerformances = lazy(() =>
  import("./board/SpectatorInsights").then((m) => ({ default: m.StandoutPerformances })),
);

/**
 * The bracket canvas is the same component the admin editor uses, so the two
 * views can never disagree about layout or about where a team can go. It is
 * lazily imported because the board itself lives in the MAIN chunk (an OBS
 * source or a QR landing must paint off one fetch), and a pan/zoom canvas has
 * no business in that budget: it loads when a bracket is actually on screen.
 */
const BracketView = lazy(() =>
  import("../components/BracketView").then((m) => ({ default: m.BracketView })),
);

/**
 * Public spectator page: live score per field, standings from finished games,
 * and the day's schedule.
 *
 * LIGHT ground, like every other surface in this app. It used to be broadcast
 * dark to match the stream, which read well indoors and badly in the situation
 * it is actually opened in: a phone held at a fence in daylight, off a QR
 * code. Ambient light adds a roughly constant reflected luminance to both
 * foreground and background, which barely moves a light screen's contrast
 * ratio and collapses a dark one's. The broadcast identity survives in the
 * dark band across the top, the pink accents and the NXT display face.
 */
export function BoardPage() {
  useGlassSurface();
  const t = useT();
  const { id: tid, slug, meta, api } = useTournament();
  /**
   * ONE request for everything the board reads, on a 60 s poll.
   *
   * This surface is a QR landing on the Free plan's 100k-requests-a-day
   * budget, and it used to make ten reads every 30 s per open tab —
   * `shared/board.ts` has the arithmetic that turned that into this bundle.
   * The publish-dependent slices (spirit, awards) still go through the same
   * DO-side projections their standalone routes use, so the toggles are
   * decided in exactly one place; an event that publishes nothing simply gets
   * empty slices inside the one response it was making anyway. The live score
   * itself rides the per-field WebSocket, not this poll.
   */
  const bundleQuery = useQuery(boardBundleQuery(tid));
  const bundle = bundleQuery.data;
  // The FIRST load only: after one success TanStack keeps the last data
  // through any error, and the socket-driven offline badge carries the story.
  // Before it, every section rendered structurally empty on a failed QR-wifi
  // fetch — "a blank card at a pitch reads as broken", per lib/queries.ts —
  // with nothing saying why and nothing to tap.
  const firstLoad = bundle === undefined;
  const teams = bundle?.teams ?? [];
  const schedule = bundle?.schedule ?? [];
  const divisions = bundle?.divisions ?? [];
  const pools = bundle?.pools ?? [];
  const stats = bundle?.stats ?? [];
  const fields = bundle?.fields ?? [];
  const settings = bundle?.settings ?? DEFAULT_SETTINGS;
  const spirit = bundle?.spirit ?? EMPTY_SPIRIT;
  const persons: Person[] = bundle?.persons ?? [];
  const awards: PublicAward[] = bundle?.awards ?? [];
  // The viewer's own squads here, if any: one tap into their workspace from
  // the page the app opens on, instead of only through My account.
  const heldIds = useHeldTeams(tid, teams);
  const mine = teams.filter((team) => heldIds.includes(team.id));

  // Which column the leaderboards rank by. There is no default "best player"
  // column and no blended score: the reader picks what they are comparing.
  const [sortKey, setSortKey] = useState<StatKey>("goals");

  useEffect(() => {
    document.title = t("board.docTitle");
  }, [t]);

  const ctx = slotContext(teams, schedule);
  // The pips on the standings: a team's finished games, seen from its side.
  const formOf = (team: Team): FormPip[] =>
    teamGamesOf(team, schedule, ctx, labelCtx).flatMap((g) =>
      g.score === undefined
        ? []
        : [
            {
              id: g.id,
              pip:
                g.score.forUs > g.score.against ? "W" : g.score.forUs < g.score.against ? "L" : "D",
            } as const,
          ],
    );
  const labelCtx = { teams, divisions, schedule, pools, fields };
  // One table per division: an open team's record must never include a
  // women's game (and vice versa). One table per pool when a division has
  // them, otherwise one flat table.
  const tables = activeDivisions(teams, divisions).map((d) => ({
    division: d,
    groups: divisionTables(teams, schedule, d.id, pools),
  }));
  const filters = scheduleFilters(useSearch({ strict: false }));
  const filtered = filterSchedule(schedule, filters, ctx);
  const upcoming = filtered.filter((g) => g.result === undefined);
  const finished = [...filtered].filter((g) => g.result !== undefined).reverse();

  // Player leaderboards come from the SHARED aggregation, per division, so the
  // board, the admin and any award argument are looking at one set of numbers.
  const statsInput = { stats, teams, schedule };
  const identity = identityFrom(persons);
  const playerTotals = playerStats(statsInput, identity);
  const credited = withCredit(playerTotals);
  const divisionSections: LeaderSection[] = [
    ...activeDivisions(teams, divisions).map((d) => ({
      key: d.id,
      title: d.name,
      rows: inDivision(credited, d.id),
      coverage: divisionCoverage(statsInput, d.id),
    })),
    // Teams with no division still played: a bucket, not a hole.
    {
      key: UNASSIGNED_DIVISION,
      title: divisionNameOf(divisions, undefined),
      rows: inDivision(credited, UNASSIGNED_DIVISION),
      coverage: divisionCoverage(statsInput, UNASSIGNED_DIVISION),
    },
  ].filter((s) => s.rows.length > 0);
  // ADDITIVE: the combined table is a second fold of the same games, appended
  // to the per-division ones and never a replacement for them. Pointless with
  // one division (it would be a copy of the only table), so it is not built.
  const leaderSections: LeaderSection[] =
    settings.stats.combined && divisionSections.length > 1
      ? [
          ...divisionSections,
          {
            key: "__combined__",
            title: t("board.allDivisions"),
            rows: withCredit(combinedStats(statsInput, identity)),
            coverage: tournamentCoverage(statsInput),
          },
        ]
      : divisionSections;

  // Resolved HERE, not inside the section, so the jump chip and the section
  // agree: an award whose player has since left every roster renders nothing,
  // and a chip pointing at an empty anchor is worse than no chip.
  const awardRows = resolveAwards(awards, teams, playerTotals, divisions, identity);
  const podiumRows = tournamentPodium({ teams, schedule, divisions, pools });
  const spiritAwardRows = resolveSpiritAwards(bundle?.spiritAwards ?? [], teams, divisions);

  // Rows arrive FOLDED from the server (`publicSpiritPayload`): shipping raw
  // submissions for a client-side fold made the standings payload exactly the
  // per-game breakdown the standings setting promises to withhold.
  const tableSections = boardTableSections(tables, schedule, spirit);
  const spiritGames =
    spirit.visibility === "games" || spirit.visibility === "full" ? spirit.games : [];

  /**
   * ONE socket per field for the whole page, held here rather than inside a
   * card.
   *
   * The summary card and the big card below it show the same game. If each
   * subscribed, every phone at the tournament would open two sockets per field
   * — the wrong direction immediately after halving what those sockets carry.
   * `FieldFeed` renders nothing; it subscribes and publishes upward, and the
   * header's status is read off the same map instead of opening its own.
   */
  const [games, setGames] = useState<Record<string, LiveGame>>({});
  const reportGame = useCallback((f: FieldId, game: LiveGame): void => {
    setGames((prev) => ({ ...prev, [f]: game }));
  }, []);
  const anyOnline = Object.values(games).some((g) => g.connected);
  const feed =
    firstLoad || Object.keys(games).length === 0
      ? "connecting"
      : anyOnline
        ? "connected"
        : "reconnecting";

  /**
   * The board is TABBED, not one long scroll.
   *
   * A spectator at a fence wants one answer (what is the score, who plays
   * next, where is my team in the table), and stacking every section into a
   * single column made all six of them somebody else's answer to scroll past.
   * Only the views that have data are offered.
   *
   * The view lives in the URL hash, so the existing `#leaders` style links and
   * QR codes still land in the right place and a view is still shareable.
   */
  // An ARCHIVED tournament is final (V2 Phase 4): no live view, no sockets,
  // the results and tables are what is left to read.
  // PAST BY THE CALENDAR with nothing left to play reads the same way (board
  // redesign, round 8): the live view would say "offline" and the schedule
  // "all played" for ever, so neither is offered and no socket is opened.
  // Dates gone by with games still unplayed keeps both: the schedule is the
  // one place those games are still visible.
  const calendarNow = useCalendarNow();
  const lifecycle = tournamentState(meta, schedule, calendarNow);
  const past = lifecycle === "ended";
  // TAKING REGISTRATION (2026-09-11): public, and nothing is being played -
  // no live view, no sockets, and the header says what the event is doing.
  const registering = meta.status === "registration";
  // NOT STARTED (2026-09-16): the same silence. A published event whose
  // first day is still ahead offered «Наживо» as its first tab, and the tab
  // said nothing was on - the schedule is the answer that early.
  const notStarted = lifecycle === "upcoming";
  const quiet = past || registering || notStarted;
  const registrationInfo = showsTournamentInfo(meta, schedule, calendarNow);
  const showInfo = registrationInfo || hasTournamentInformation(meta.information);
  const views: { id: BoardView; label: string }[] = [
    ...(registrationInfo ? [{ id: "info" as const, label: t("board.view.info") }] : []),
    ...(quiet ? [] : [{ id: "live" as const, label: t("board.view.live") }]),
    ...(tableSections.length > 0 ? [{ id: "tables" as const, label: t("board.view.tables") }] : []),
    ...(teams.length > 0 ? [{ id: "teams" as const, label: t("board.view.teams") }] : []),
    ...(past ? [] : [{ id: "next" as const, label: t("board.view.next") }]),
    ...(schedule.some((g) => g.result !== undefined)
      ? [{ id: "results" as const, label: t("board.view.results") }]
      : []),
    ...(schedule.some((g) => g.result !== undefined)
      ? [{ id: "highlights" as const, label: t("spectator.highlights") }]
      : []),
    ...(leaderSections.length > 0
      ? [{ id: "leaders" as const, label: t("board.view.leaders") }]
      : []),
    ...(awardRows.length > 0 || spiritAwardRows.length > 0 || podiumRows.length > 0
      ? [{ id: "awards" as const, label: t("board.view.awards") }]
      : []),
    ...(teams.length > 0 ? [{ id: "fans" as const, label: t("fan.title") }] : []),
    ...(showInfo && !registrationInfo
      ? [{ id: "info" as const, label: t("board.view.info") }]
      : []),
  ];
  const [view, setView] = useState<BoardView>(() => hashView());
  // The Live view's pitch rides the same hash (`#live/<field>`), so a link to
  // one pitch's game is shareable like a view is. Null until the viewer taps
  // a pill: the view then opens on the pitch `openingPitch` picks.
  const [pitch, setPitch] = useState<FieldId | null>(() => hashPitch());
  // A standings row opens the team's own page (board redesign, round 4).
  const navigate = useNavigate();
  const openTeam = (team: Team): void => {
    void navigate({ to: "/t/$slug/teams/$teamId", params: { slug, teamId: team.id } });
  };
  // A result opens the game's own page (board redesign, round 5).
  const openGame = (game: ScheduledGame): void => {
    void navigate({ to: "/t/$slug/games/$gameId", params: { slug, gameId: game.id } });
  };
  useEffect(() => {
    const sync = (): void => {
      setView(hashView());
      setPitch(hashPitch());
    };
    window.addEventListener("hashchange", sync);
    window.addEventListener("popstate", sync);
    return () => {
      window.removeEventListener("hashchange", sync);
      window.removeEventListener("popstate", sync);
    };
  }, []);
  // A view whose data has since gone (the last result deleted) must not leave
  // the page blank.
  // Open registration leads with practical event information; after the door
  // closes, a published upcoming event still lands on its schedule. Completed
  // events lead with their resolved podiums; explicit hashes still win below.
  const landing =
    (past && hasCompletePodiums(teams, schedule, podiumRows)
      ? views.find((v) => v.id === "awards")
      : undefined) ??
    (registrationInfo ? views.find((v) => v.id === "info") : undefined) ??
    (notStarted ? views.find((v) => v.id === "next") : undefined) ??
    views[0];
  const active = views.some((v) => v.id === view) ? view : (landing?.id ?? "live");
  // Verified public portraits for scoring moments and the player views;
  // cached separately from the board's poll.
  const profiles = useQuery({
    ...tournamentProfilesQuery(tid),
    enabled: active === "live" || active === "leaders" || active === "awards",
  }).data;
  const profileById = new Map((profiles ?? []).map((p) => [p.playerId, p]));
  const selectView = (next: BoardView): void => {
    setView(next);
    setPitch(null);
    window.history.replaceState(null, "", `#${next}`);
  };
  const selectPitch = (next: FieldId): void => {
    setPitch(next);
    window.history.replaceState(null, "", `#live/${encodeURIComponent(next)}`);
  };
  const changeFilters = (next: ScheduleFilters): void => {
    void navigate({
      to: "/t/$slug",
      params: { slug },
      search: next,
      hash: active,
      replace: true,
      resetScroll: false,
    });
  };
  const pulse = tournamentPulse(
    schedule,
    quiet
      ? []
      : fields.flatMap((field) => {
          const feed = games[field.id];
          return feed ? [feed] : [];
        }),
  );
  const pageLinks = [
    ...(!past ? [{ label: t("flow.pickups"), href: tournamentHref(slug, "/pickups") }] : []),
    ...(bundle?.regulations != null
      ? [{ label: t("board.regulationsLink"), href: tournamentHref(slug, "/regulations") }]
      : []),
  ];

  return (
    <div className="min-h-dvh pb-12">
      <div className="board-ground" aria-hidden="true" />
      <AppHeader />
      <div className="mx-auto w-full max-w-3xl space-y-5 px-4 pt-6 adm:max-w-6xl">
        <TournamentBanner
          meta={meta}
          teams={bundle?.teams}
          schedule={bundle?.schedule}
          pulse={bundle ? pulse : undefined}
          liveUnavailable={!quiet && fields.some((field) => !games[field.id]?.connected)}
          compact={active === "live"}
        />
        <SectionNav
          title={meta.short ?? meta.name}
          views={views}
          active={active}
          onSelect={selectView}
          links={pageLinks}
          trailing={
            mine.length > 0 ? (
              <TeamWorkspaceShortcuts slug={slug} teams={mine} divisions={divisions} />
            ) : undefined
          }
          // On a phone the same squads are a section of the sheet, each row
          // led by its code chip (owner, 2026-09-15).
          menuSections={
            mine.length > 0
              ? [
                  {
                    id: "your-teams",
                    label: t("board.team.yourTeams"),
                    links: mine.map((team) => {
                      const label = heldTeamLabel(team, mine, divisions);
                      return {
                        label,
                        href: tournamentHref(slug, `/team/${team.id}`),
                        lead: <TeamChip short={team.short} color={team.color} size="md" />,
                        ariaLabel: t("board.team.yourTeamLink", { short: team.short, team: label }),
                      };
                    }),
                  },
                ]
              : []
          }
        />
      </div>

      {/* First load only, and BELOW the tab strip, where the content it stands
          in for belongs: above it the message read as a broken header while
          the empty sections underneath ("no game right now") contradicted it.
          After one success the last data survives any error and the offline
          badge tells the story. */}
      {firstLoad && (
        <div className="mx-auto w-full max-w-3xl adm:max-w-6xl px-4 py-6 text-center text-sm text-muted-foreground">
          {bundleQuery.isError ? (
            <>
              <p>{t("board.loadFailed")}</p>
              <Button variant="outline" className="mt-3" onClick={() => void bundleQuery.refetch()}>
                {t("common.retry")}
              </Button>
            </>
          ) : (
            <p>{t("common.loading")}</p>
          )}
        </div>
      )}

      {/* The page's sockets. Mounted regardless of the active view so a
          spectator switching back to Live is not waiting on a reconnect, and
          so the header's status is honest on every tab. */}
      {!quiet && fields.map((f) => <FieldFeed key={f.id} field={f.id} onGame={reportGame} />)}

      {/* Nothing below until the first bundle is in: empty sections under a
          "could not load" message tell a spectator two stories at once. */}
      {!firstLoad && (
        <div
          key={active}
          className="board-enter animate-in fade-in-0 slide-in-from-bottom-2 mx-auto w-full max-w-3xl adm:max-w-6xl space-y-8 px-4 pt-6 duration-300"
        >
          {active === "info" && bundle !== undefined && (
            <TournamentInfo meta={meta} bundle={bundle} registrationOpen={registrationInfo} />
          )}
          {active === "live" && (
            <LiveView
              fields={fields}
              games={games}
              teams={teams}
              schedule={schedule}
              pools={pools}
              divisions={divisions}
              ctx={ctx}
              labelCtx={labelCtx}
              mine={mine}
              feed={feed}
              pitch={pitch}
              onPitch={selectPitch}
              onOpenGame={openGame}
              profiles={profileById}
            />
          )}

          {/*
          Order follows the day: while a division is still in pool play its
          table is the story, and its bracket is a wall of "OPEN 1"
          placeholders. Once every pool game has a result the seeds resolve, so
          the bracket becomes the story and leads instead.
        */}
          {active === "tables" &&
            tableSections.map((tbl) => {
              const { decided, progress, showBracket, bracket, spiritRows } = tbl;

              const standings = tbl.groups.map((g) => (
                <Section
                  key={g.key}
                  title={[tbl.division.name, g.title]
                    .filter((x) => x !== null)
                    .join(" · ")
                    .toUpperCase()}
                  note={
                    // The table's own games: a pool's three, not the
                    // division's six.
                    !decided && g.progress.total > 0
                      ? t("board.poolProgress", {
                          played: g.progress.played,
                          total: g.progress.total,
                        })
                      : undefined
                  }
                >
                  <StandingsTable rows={g.rows} onTeam={openTeam} form={formOf} />
                </Section>
              ));

              const bracketSection =
                bracket.length === 0 ? null : (
                  <Section
                    key={`br-${tbl.division.id}`}
                    title={t("board.bracketTitle", { division: tbl.division.name.toUpperCase() })}
                    note={showBracket ? undefined : t("board.afterPool")}
                  >
                    {showBracket ? (
                      <Suspense
                        fallback={
                          <Card className="p-4 text-sm text-muted-foreground">
                            {t("board.loadingBracket")}
                          </Card>
                        }
                      >
                        <BracketView
                          games={bracket}
                          ctx={ctx}
                          labelCtx={labelCtx}
                          mode="view"
                          ground="chalk"
                        />
                      </Suspense>
                    ) : (
                      <Card className="p-4 text-sm text-muted-foreground text-pretty">
                        {t("board.seedsPending", {
                          division: tbl.division.name,
                          count: progress.total - progress.played,
                        })}
                      </Card>
                    )}
                  </Section>
                );

              // Pools side by side where the width allows; a lone pool takes the
              // whole row (refinements, 2026-09-09). "Allows" means a name
              // still fits on its one line: at 24rem a small laptop put two
              // cards of ~450px side by side and «Odesa Ultimate» kept five
              // letters, so two-up waits for ~34rem a card (2026-09-14).
              const standingsBlock =
                standings.length === 0 ? null : (
                  <div
                    key={`st-${tbl.division.id}`}
                    className="grid gap-6 adm:grid-cols-[repeat(auto-fit,minmax(34rem,1fr))]"
                  >
                    {standings}
                  </div>
                );
              // The division's spirit ranking, under its tables: it had a tab of
              // its own, and a ranking belongs with the rankings.
              const spiritSection =
                spiritRows.length === 0 ? null : (
                  <Section
                    key={`sp-${tbl.division.id}`}
                    title={`${t("board.spiritTitle")} · ${tbl.division.name.toUpperCase()}`}
                  >
                    <SpiritTable rows={spiritRows} minGames={settings.spirit.minGames} />
                  </Section>
                );

              return (
                <Fragment key={tbl.division.id}>
                  {showBracket ? (
                    <>
                      {bracketSection}
                      {standingsBlock}
                    </>
                  ) : (
                    <>
                      {standingsBlock}
                      {bracketSection}
                    </>
                  )}
                  {spiritSection}
                </Fragment>
              );
            })}

          {active === "teams" && <TeamsDirectory tables={tables} teams={teams} />}

          {(active === "next" || active === "results") && (
            <Suspense fallback={<p>{t("common.loading")}</p>}>
              <ScheduleFilterBar
                value={filters}
                onChange={changeFilters}
                ctx={ctx}
                labelCtx={labelCtx}
                shown={active === "next" ? upcoming.length : finished.length}
                total={
                  schedule.filter((g) =>
                    active === "next" ? g.result === undefined : g.result !== undefined,
                  ).length
                }
              />
            </Suspense>
          )}
          {((active === "next" && upcoming.length === 0) ||
            (active === "results" && finished.length === 0)) &&
            Object.keys(filters).length > 0 && (
              <p className="text-sm text-muted-foreground">{t("spectator.noMatches")}</p>
            )}
          {active === "fans" && (
            <Suspense fallback={<p>{t("common.loading")}</p>}>
              <FanZone />
            </Suspense>
          )}
          {active === "highlights" && (
            <Suspense fallback={<p>{t("common.loading")}</p>}>
              <StandoutPerformances ctx={ctx} labelCtx={labelCtx} stats={stats} persons={persons} />
            </Suspense>
          )}
          {active === "next" && (upcoming.length > 0 || Object.keys(filters).length === 0) && (
            <UpNext
              games={upcoming}
              fields={fields}
              ctx={ctx}
              labelCtx={labelCtx}
              empty={schedule.length === 0}
            />
          )}

          {active === "results" && finished.length > 0 && (
            <Results
              games={finished}
              spirit={spiritGames}
              ctx={ctx}
              labelCtx={labelCtx}
              onOpen={openGame}
            />
          )}

          {active === "leaders" && (
            <Section
              title={t("board.leadersTitle")}
              aside={
                <Link
                  to="/t/$slug/players"
                  params={{ slug }}
                  search={{ sort: sortKey }}
                  className={buttonVariants({ variant: "outline" })}
                >
                  {t("tournamentPlayers.all")}
                  <ChevronRightIcon aria-hidden="true" />
                </Link>
              }
            >
              <Leaders
                sections={leaderSections}
                minGames={settings.stats.minGames}
                perGame={settings.stats.perGame}
                sortKey={sortKey}
                onSort={setSortKey}
                profiles={profileById}
              />
            </Section>
          )}

          {active === "awards" && (
            <Section title={t("board.awardsTitle")}>
              <Awards
                rows={awardRows}
                spiritRows={spiritAwardRows}
                podiumRows={podiumRows}
                slug={slug}
                divisions={divisions}
                profiles={profileById}
              />
            </Section>
          )}

          {/* The calendar feed (ROADMAP §5). In the footer rather than the phone
            menu because the menu is the phone's alone, and this is the one
            thing on the page a laptop reader wants to take away with them. */}
          <div className="flex flex-col items-center gap-2 pt-4">
            {/* Not `.eyebrow`, unlike the wordmark under it: this is a sentence,
              and 0.3em of tracking on one wraps a phone's footer into three
              lines to say a thing that fits on one. */}
            {(active !== "info" || schedule.length > 0) && (
              <a className="text-xs text-brand-ink hover:underline" href={api.paths.ics()}>
                {t("board.calendar")}
              </a>
            )}
            <div className="eyebrow text-muted-foreground">SCORE.KARRTOPELKA.COM</div>
          </div>
        </div>
      )}
    </div>
  );
}

const FanZone = lazy(() => import("./board/FanEngagement").then((m) => ({ default: m.FanZone })));
const MatchPrediction = lazy(() =>
  import("./board/FanEngagement").then((m) => ({ default: m.MatchPrediction })),
);

type BoardView =
  | "info"
  | "live"
  | "tables"
  | "teams"
  | "next"
  | "results"
  | "leaders"
  | "awards"
  | "highlights"
  | "fans";

const BOARD_VIEWS: BoardView[] = [
  "info",
  "live",
  "tables",
  "teams",
  "next",
  "results",
  "leaders",
  "awards",
  "highlights",
  "fans",
];

/** The old anchor links (`#leaders`, `#standings`) still work. The part after
 *  a slash is the Live view's pitch (`#live/second-field`), read by
 *  `hashPitch`; a build from before it existed lands on Live all the same,
 *  because an unknown hash always has. */
function hashView(): BoardView {
  const raw = window.location.hash.replace("#", "").split("/")[0] ?? "";
  // `#spirit` was a tab until 2026-09-09; its ranking now sits under the tables.
  const legacy: Record<string, BoardView> = {
    standings: "tables",
    schedule: "next",
    spirit: "tables",
  };
  const mapped = legacy[raw] ?? raw;
  return BOARD_VIEWS.includes(mapped as BoardView) ? (mapped as BoardView) : "live";
}

/** The pitch a `#live/<field>` link names, or null. Whether that pitch still
 *  exists is the view's call: a stale link opens the pitch it would have. */
function hashPitch(): FieldId | null {
  const [view, field] = window.location.hash.replace("#", "").split("/");
  if (view !== "live" || field === undefined || field === "") return null;
  try {
    return decodeURIComponent(field);
  } catch {
    return null;
  }
}

const EMPTY_SPIRIT: PublicSpiritPayload = { visibility: "private", standings: [], games: [] };

/** A sub-table's heading inside a section. Same face, quieter. */
function SubHead({ children, note }: { children: React.ReactNode; note?: string }) {
  return (
    <h3 className="mb-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <span className="nxt text-sm text-muted-foreground">{children}</span>
      {note !== undefined && (
        <span className="text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
          {note}
        </span>
      )}
    </h3>
  );
}

/** A small dim aside. Works block or inline. */
function Note({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={cn("text-sm leading-relaxed text-muted-foreground text-pretty", className)}>
      {children}
    </p>
  );
}

// ---------- live field card ----------

/**
 * One field's socket, and nothing on screen.
 *
 * Renders `null` on purpose: the game it subscribes to is drawn in two places
 * on this page, so the subscription cannot belong to either of them.
 */
function FieldFeed({
  field,
  onGame,
}: {
  field: FieldId;
  onGame: (field: FieldId, game: LiveGame) => void;
}) {
  const { state, skew, connected, obs, undoDepth, undoKind, nudgeObs } = useGameState(field, {
    src: "board",
  });
  // Spread rather than passing the hook's object: it is a fresh object every
  // render, and depending on it would publish upward on every tick of the
  // clock the card above is already re-rendering for. `nudgeObs` is a stable
  // callback, so carrying it costs nothing (and the board never calls it).
  useEffect(() => {
    onGame(field, { state, skew, connected, obs, undoDepth, undoKind, nudgeObs });
  }, [field, state, skew, connected, obs, undoDepth, undoKind, nudgeObs, onGame]);
  return null;
}

// ---------- standings ----------

/** One of the last five results, as the pip strip reads it. */
interface FormPip {
  id: string;
  pip: "W" | "L" | "D";
}

/**
 * A division's table as the board reads it (board redesign, 2026-09-10): the
 * rank in the display face, the team as its coloured code beside the name
 * (the same chip the live card wears, so a team looks like itself on every
 * view), played and the differential in tabular Inter with the differential
 * tinted by sign, and on a laptop the last five results as a strip of pips -
 * a spectator reads "who is hot" off the pips before the arithmetic.
 *
 * The BUTTON is the name, not the row: a row-level onClick has no keyboard
 * story and announces nothing, and the name is the thing being asked about.
 */
function StandingsTable({
  rows,
  onTeam,
  form,
}: {
  rows: StandingRow[];
  /** A tapped name opens that team's sheet (roster, numbers, games). */
  onTeam?: (team: Team) => void;
  /** The team's finished games, oldest first; the strip shows the last five. */
  form?: (team: Team) => FormPip[];
}) {
  const t = useT();
  const anyDraw = rows.some((r) => r.draws > 0);
  // The phone drops the played column (W-L implies it) and narrows the chip
  // so a name like «Odesa Ultimate» keeps its letters; the laptop adds
  // played and the pips. The last column is the chevron: the row opens the
  // team's page.
  // Every row is ONE line high (owner, 2026-09-14): a name that wrapped made
  // one row taller than its neighbours and the table stopped reading as rows.
  // So the name truncates, and the number columns are sized to what they
  // carry ("10-2", "+12", five pips) rather than padded, which gave two pools
  // side by side on a laptop so little name room that «Gigolo Black» and
  // «Odesa Ultimate» broke. The full name is in the tooltip and on the page
  // the row opens.
  const cols =
    "grid-cols-[1.5rem_minmax(0,1fr)_2.75rem_2.25rem_0.75rem] gap-x-2 adm:grid-cols-[2rem_minmax(0,1fr)_2rem_3rem_2.5rem_4.75rem_1rem] adm:gap-x-3";
  return (
    <Card className="gap-0 overflow-hidden p-0">
      <div
        className={cn(
          "grid items-center border-b px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground",
          cols,
        )}
      >
        <span />
        <span>{t("board.col.team")}</span>
        <span className="hidden text-right adm:block">{t("board.col.played")}</span>
        <span className="text-right">
          {anyDraw ? t("board.col.winDrawLoss") : t("board.col.winLoss")}
        </span>
        <span className="text-right">{t("board.col.diff")}</span>
        <span className="hidden text-right adm:block">{t("board.col.form")}</span>
        <span aria-hidden="true" />
      </div>
      {rows.map((row, i) => {
        const pips = form?.(row.team).slice(-5) ?? [];
        return (
          // The whole row is the tap target and the chevron at its end says
          // so; the name alone as the link left the rest of the row dead to a
          // tap, and nothing said the name was one.
          <button
            type="button"
            key={row.team.id}
            onClick={() => onTeam?.(row.team)}
            className={cn(
              "grid w-full items-center border-b px-3 py-2 text-left transition-colors last:border-b-0 hover:bg-accent",
              cols,
              i === 0 && "board-leader",
            )}
          >
            <span
              className={cn(
                "nxt tabular text-lg leading-none",
                i < 3 ? "text-foreground" : "text-muted-foreground",
              )}
            >
              {i + 1}
            </span>
            <span className="flex min-w-0 items-center gap-2">
              <TeamCode team={row.team} />
              <span className="min-w-0 truncate font-medium" title={row.team.name}>
                {row.team.name}
              </span>
            </span>
            <span className="tabular hidden text-right text-sm text-muted-foreground adm:block">
              {row.played}
            </span>
            <span className="tabular text-right text-sm font-semibold">
              {anyDraw ? `${row.wins}-${row.draws}-${row.losses}` : `${row.wins}-${row.losses}`}
            </span>
            <span
              className={cn(
                "tabular text-right text-sm font-semibold",
                row.diff > 0 ? "text-go-ink" : "text-muted-foreground",
              )}
            >
              {row.diff > 0 ? `+${row.diff}` : row.diff}
            </span>
            <span className="hidden justify-end gap-1 adm:flex">
              {pips.map((p) => (
                <span
                  key={p.id}
                  title={p.pip}
                  className={cn(
                    "h-1.5 w-3 rounded-sm",
                    p.pip === "W"
                      ? "bg-go"
                      : p.pip === "L"
                        ? "bg-muted-foreground/35"
                        : "bg-muted-foreground/70",
                  )}
                />
              ))}
            </span>
            <ChevronRightIcon
              aria-hidden="true"
              className="size-4 justify-self-end text-muted-foreground/60"
            />
          </button>
        );
      })}
    </Card>
  );
}

/** A team's code on its own colour: the live card's chip, at the size a row
 *  can carry. The one way a team is drawn on this page, so it looks like
 *  itself on the table, the schedule and the result. */
/**
 * Every team of the event, by division: the board's directory (board
 * redesign, round 14). A card per team - its crest and chip, full name, pool
 * and its record - opening the team's page, where its games, its roster and
 * the other events it has played are. A team in no division (a draft's, an
 * event with none) sits in a last section of its own rather than nowhere.
 */
function TeamsDirectory({
  tables,
  teams,
}: {
  tables: {
    division: Division;
    groups: { key: string; title: string | null; rows: StandingRow[] }[];
  }[];
  teams: Team[];
}) {
  const t = useT();
  const seen = new Set<string>();
  const sections = tables.map((tbl) => {
    const rows = tbl.groups.flatMap((g) => g.rows.map((row) => ({ row, pool: g.title })));
    for (const r of rows) seen.add(r.row.team.id);
    return { key: tbl.division.id, title: tbl.division.name.toUpperCase(), rows };
  });
  const rest = teams.filter((x) => !seen.has(x.id));
  const grid = "grid grid-cols-2 gap-3 sm:gap-4 adm:grid-cols-3";
  return (
    <>
      {sections.map((s) =>
        s.rows.length === 0 ? null : (
          <Section
            key={s.key}
            title={s.title}
            note={t("board.teamsCount", { count: s.rows.length })}
          >
            <div className={grid}>
              {s.rows.map(({ row, pool }) => (
                <TeamCard key={row.team.id} team={row.team} pool={pool} row={row} />
              ))}
            </div>
          </Section>
        ),
      )}
      {rest.length > 0 && (
        <Section
          title={t("board.view.teams").toUpperCase()}
          note={t("board.teamsCount", { count: rest.length })}
        >
          <div className={grid}>
            {rest.map((team) => (
              <TeamCard key={team.id} team={team} pool={null} row={null} />
            ))}
          </div>
        </Section>
      )}
    </>
  );
}

/** Reuse the club gallery's identity treatment with the event's own artwork
 * and record. An event squad may have a different crest from its parent club. */
function TeamCard({
  team,
  pool,
  row,
}: {
  team: Team;
  pool: string | null;
  row: StandingRow | null;
}) {
  const { slug } = useTournament();
  const record =
    row === null
      ? null
      : row.draws > 0
        ? `${row.wins}-${row.draws}-${row.losses}`
        : `${row.wins}-${row.losses}`;
  const line = [pool, record].filter((x): x is string => x !== null).join(" · ");
  return (
    <Link
      to="/t/$slug/teams/$teamId"
      params={{ slug, teamId: team.id }}
      className="board-pane relative flex min-w-0 flex-col rounded-2xl border bg-card p-3 transition-colors hover:bg-accent focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring sm:p-5"
    >
      <ArrowUpRightIcon
        aria-hidden="true"
        className="absolute right-3 top-3 size-4 text-muted-foreground sm:right-5 sm:top-5"
      />
      <ClubIdentity
        name={team.name}
        logo={team.logo}
        logoDark={team.logoDark}
        short={team.short}
        color={team.color}
        headingAs="h3"
      />
      {line !== "" && (
        <span className="tabular mt-2 flex flex-1 items-end justify-between gap-2 text-xs text-muted-foreground sm:text-sm">
          <span>{line}</span>
          {row !== null && row.played > 0 && (
            <span className={cn("shrink-0 font-semibold", row.diff > 0 && "text-go-ink")}>
              {row.diff > 0 ? `+${row.diff}` : row.diff}
            </span>
          )}
        </span>
      )}
    </Link>
  );
}

/** The shared chip (`components/app/team-chip`) with a `Team` in hand. */

// ---------- schedule / results row ----------

/**
 * What has been played, newest first and SPLIT BY DAY.
 *
 * A flat list was fine for one afternoon and unreadable for a weekend: two
 * days of results in one column with 09:00 appearing twice is exactly the
 * thing the captain's hub splits for ("a two-day tournament reads as one
 * undifferentiated list otherwise"), and the schedule half of this page has
 * split by day all along.
 *
 * Days descend as the games do, so the most recent result is the first thing
 * on the screen.
 */
function Results({
  games,
  spirit,
  ctx,
  labelCtx,
  onOpen,
}: {
  games: ScheduledGame[];
  /** Published spirit scores, matched to their game: the Spirit tab's
   *  per-game list lives on the results now (2026-09-09). */
  spirit: PublicSpiritGame[];
  ctx: ReturnType<typeof slotContext>;
  labelCtx: LabelCtx;
  /** A tapped row opens the game sheet: point-by-point, and the card link. */
  onOpen: (game: ScheduledGame) => void;
}) {
  const t = useT();
  if (games.length === 0) {
    return (
      <Section title={t("board.results")}>
        <Card className="p-4 text-sm text-muted-foreground">{t("board.noResults")}</Card>
      </Section>
    );
  }

  // The DECIDING games first (round 8): each division's final and third-place
  // game, pulled out of the day lists into their own row of floating panes,
  // because on a finished tournament "who won" is the whole question and it
  // was one row among forty. `effectiveLabel` says which games those are -
  // the rule the team record and the result card already use - so a retyped
  // note never promotes a pool game. Division order, the final before the
  // third-place game.
  const rank = (g: ScheduledGame): number => {
    const i = labelCtx.divisions.findIndex((d) => d.id === g.divisionId);
    return i < 0 ? labelCtx.divisions.length : i;
  };
  const podium = games
    .filter((g) => decidingOf(g) !== undefined)
    .sort(
      (x, y) =>
        rank(x) - rank(y) ||
        (decidingOf(x) === "final" ? 0 : 1) - (decidingOf(y) === "final" ? 0 : 1) ||
        x.startAt.localeCompare(y.startAt),
    );

  // `games` arrives newest first, so insertion order gives newest day first.
  const byDay = new Map<string, ScheduledGame[]>();
  for (const g of games) {
    if (decidingOf(g) !== undefined) continue;
    const day = g.startAt.slice(0, 10);
    byDay.set(day, [...(byDay.get(day) ?? []), g]);
  }

  return (
    <>
      {podium.length > 0 && (
        <Section title={t("board.finals")} note={t("board.gamesCount", { count: podium.length })}>
          <div className="grid gap-3 adm:grid-cols-2">
            {podium.map((g) => (
              <DecidingCard
                key={g.id}
                game={g}
                kind={decidingOf(g) ?? "final"}
                spirit={spirit.find((x) => x.gameId === g.id)}
                ctx={ctx}
                labelCtx={labelCtx}
                onOpen={onOpen}
              />
            ))}
          </div>
        </Section>
      )}

      {[...byDay.entries()].map(([day, list]) => (
        <Section
          key={day}
          title={dayHeading(day)}
          note={t("board.gamesCount", { count: list.length })}
        >
          {/* Full width, like every other block on this page.
              This was capped at `max-w-2xl` first, borrowing the admin
              schedule's reasoning that a wide card puts a score half a metre
              from the name it belongs to. That reasoning holds for the admin,
              whose list is a page of its own, and not here: the board's views
              are TABS in one shell, so a narrower column made the content edge
              jump every time a reader switched between Results and anything
              else. One edge down the page beats a tidier gap on one tab. */}
          {/* Two-up on a laptop; a day with one game keeps the whole row. */}
          <div className="grid gap-1.5 adm:grid-cols-[repeat(auto-fit,minmax(24rem,1fr))]">
            {list.map((g) => (
              <ResultRow
                key={g.id}
                game={g}
                spirit={spirit.find((x) => x.gameId === g.id)}
                ctx={ctx}
                labelCtx={labelCtx}
                onOpen={onOpen}
              />
            ))}
          </div>
        </Section>
      ))}
    </>
  );
}

/** The final or the third-place game, by the label the generator wrote. */
type Deciding = "final" | "third";
function decidingOf(game: ScheduledGame): Deciding | undefined {
  const label = effectiveLabel(game.note, game.label);
  if (label === undefined) return undefined;
  if (label.kind === "final" || label.kind === "grandFinal") return "final";
  if (label.kind === "thirdPlace") return "third";
  return undefined;
}

/**
 * A deciding game as a floating pane: the round's name on a slanted chip
 * (brand for the final, ink for the third-place game), the two sides a size
 * up, the winner carrying the trophy or the medal. The same tap target as
 * `ResultRow`: the game's own page.
 */
function DecidingCard({
  game,
  kind,
  spirit,
  ctx,
  labelCtx,
  onOpen,
}: {
  game: ScheduledGame;
  kind: Deciding;
  spirit?: PublicSpiritGame | undefined;
  ctx: ReturnType<typeof slotContext>;
  labelCtx: LabelCtx;
  onOpen: (game: ScheduledGame) => void;
}) {
  const a = sideOf(game, "a", ctx, labelCtx);
  const b = sideOf(game, "b", ctx, labelCtx);
  const result = game.result;
  const wonA = result !== undefined && result.scoreA >= result.scoreB;
  const wonB = result !== undefined && result.scoreB >= result.scoreA;
  const Mark = kind === "final" ? TrophyIcon : MedalIcon;
  const mark = <Mark aria-hidden="true" className="size-4 shrink-0 text-brand" />;
  const meta = [
    divisionTagOf(labelCtx.divisions, game.divisionId),
    fieldNameOf(labelCtx.fields, game.fieldId),
    `${dayHeading(game.startAt.slice(0, 10))} ${timeOf(game.startAt)}`,
  ]
    .filter((x): x is string => x !== undefined)
    .join(" · ");

  return (
    <button
      type="button"
      onClick={() => onOpen(game)}
      className={cn(
        "board-float board-podium block w-full bg-card p-4 text-left transition-[filter] hover:brightness-110",
        kind === "final" && "board-podium-final",
      )}
    >
      <span className="flex items-center gap-3">
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span
              className={cn(
                "slant-chip team-code px-2.5 py-1 text-sm leading-none",
                kind === "final" ? "bg-brand text-brand-on" : "bg-primary text-primary-foreground",
              )}
            >
              {(gameNoteOf(game) ?? "").toUpperCase()}
            </span>
            <span className="eyebrow min-w-0 truncate text-muted-foreground">{meta}</span>
          </span>
          <span className="mt-3 block space-y-1">
            <ResultSide
              side={a}
              size="lg"
              spirit={spiritChipOf(spirit, a.team, b)}
              {...(result !== undefined ? { score: result.scoreA, won: wonA } : {})}
              {...(wonA ? { badge: mark } : {})}
            />
            <ResultSide
              side={b}
              size="lg"
              spirit={spiritChipOf(spirit, b.team, a)}
              {...(result !== undefined ? { score: result.scoreB, won: wonB } : {})}
              {...(wonB ? { badge: mark } : {})}
            />
          </span>
        </span>
        <ChevronRightIcon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground/60" />
      </span>
    </button>
  );
}

/**
 * One finished game: the two sides STACKED, each with its own number.
 *
 * Not "A vs B   21–15" on one line, and the reference is `Side` in the admin's
 * schedule list, whose reasoning applies here word for word: a matchup is two
 * things of equal weight, so neither may be the one that gets ellipsed, and a
 * reader should not have to match the order of two names against the order of
 * two numbers to work out who won. The loser's line recedes; the winner keeps
 * full ink. A draw dims neither.
 *
 * The old shape truncated the matchup to fit a score beside it, which at 375px
 * meant a result whose second team was not shown at all.
 */
function ResultRow({
  game,
  spirit,
  ctx,
  labelCtx,
  onOpen,
}: {
  game: ScheduledGame;
  spirit?: PublicSpiritGame | undefined;
  ctx: ReturnType<typeof slotContext>;
  labelCtx: LabelCtx;
  onOpen: (game: ScheduledGame) => void;
}) {
  const a = sideOf(game, "a", ctx, labelCtx);
  const b = sideOf(game, "b", ctx, labelCtx);
  const spiritOf = (team: Team | null, giver: BoardSide): SpiritChip | undefined =>
    spiritChipOf(spirit, team, giver);
  const result = game.result;
  // The division earns its place here for the same reason it does on the
  // schedule: nothing else in this list says whether it was an Open game.
  const meta = [
    divisionTagOf(labelCtx.divisions, game.divisionId),
    fieldNameOf(labelCtx.fields, game.fieldId),
    gameNoteOf(game),
  ]
    .filter((x): x is string => x !== undefined)
    .join(" · ");

  return (
    // The whole card is the tap target, the admin schedule's convention: a
    // result is one thing, and there is no sub-element that means something
    // different to tap.
    <button
      type="button"
      onClick={() => onOpen(game)}
      className="board-pane block w-full rounded-xl border bg-card px-3 py-2.5 text-left transition-colors hover:bg-accent"
    >
      {/* When and where, on the line above the result. No `.toUpperCase()`:
          `.eyebrow` is already `text-transform: uppercase`, and calling both
          reads as though only half of this line is meant to be shouted. */}
      <span className="flex items-center gap-2">
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-2">
            <span className="tabular shrink-0 text-xs font-semibold text-muted-foreground">
              {timeOf(game.startAt)}
            </span>
            <span className="eyebrow min-w-0 truncate text-muted-foreground">{meta}</span>
          </span>
          <span className="mt-1.5 block space-y-0.5">
            <ResultSide
              side={a}
              spirit={spiritOf(a.team, b)}
              {...(result !== undefined
                ? { score: result.scoreA, won: result.scoreA >= result.scoreB }
                : {})}
            />
            <ResultSide
              side={b}
              spirit={spiritOf(b.team, a)}
              {...(result !== undefined
                ? { score: result.scoreB, won: result.scoreB >= result.scoreA }
                : {})}
            />
          </span>
        </span>
        {/* The card opens the game's page, and the chevron says so. */}
        <ChevronRightIcon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground/60" />
      </span>
    </button>
  );
}

/** The spirit a side received, and from whom. */
type SpiritChip = { total: number; from: string };

/**
 * The spirit a side RECEIVED, named with its giver: "Spirit 11 from LYNX" is
 * the one reading nobody has to know the convention for ("Spirit 11" beside
 * a team read as given or received, depending on who was asking). The
 * payload attributes each total to the giver; both totals are present or the
 * game is absent.
 */
function spiritChipOf(
  spirit: PublicSpiritGame | undefined,
  team: Team | null,
  giver: BoardSide,
): SpiritChip | undefined {
  if (team === null || spirit === undefined) return undefined;
  const total = spirit.scores.find((x) => x.scoredTeamId === team.id)?.total;
  return total === undefined ? undefined : { total, from: giver.team?.short ?? giver.text };
}

function ResultSide({
  side,
  score,
  won,
  spirit,
  size = "md",
  badge,
}: {
  side: BoardSide;
  score?: number;
  won?: boolean;
  spirit?: SpiritChip | undefined;
  /** `lg` on the deciding games' panes: the name a step up, the score two. */
  size?: "md" | "lg";
  /** The winner's mark on a deciding game, beside the name. */
  badge?: ReactNode | undefined;
}) {
  const t = useT();
  const lg = size === "lg";
  return (
    // CENTRED on the row, not top-aligned: the score is the tallest thing in
    // it (text-3xl on a deciding game), and top-aligned the dot, the name, the
    // trophy and the spirit chip all sat above the number (owner, 2026-09-15).
    <span className="flex items-center gap-2">
      {side.team !== null ? (
        <ColorDot color={side.team.color} />
      ) : (
        // An undecided bracket side has no colour, and an empty ring keeps the
        // two lines on the same rhythm as a resolved pair.
        <span
          aria-hidden="true"
          className="size-2.5 shrink-0 rounded-full border border-dashed border-muted-foreground/60"
        />
      )}
      <span
        className={cn(
          "min-w-0 flex-1 leading-snug text-pretty",
          lg ? "text-base" : "text-sm",
          side.team === null
            ? "text-muted-foreground"
            : won === false
              ? "font-medium text-muted-foreground"
              : "font-medium",
        )}
      >
        {side.text}
      </span>
      {badge}
      {spirit !== undefined && (
        <span
          className="tabular inline-flex shrink-0 whitespace-nowrap rounded-md border px-1.5 py-0.5 text-[11px] font-semibold leading-none text-muted-foreground"
          title={t("board.spiritFromHint", { team: spirit.from })}
        >
          {t("board.spiritFrom", { n: spirit.total, team: spirit.from })}
        </span>
      )}
      {score !== undefined && (
        <span
          className={cn(
            // A fixed column: the display face has no tabular figures, so a "1"
            // under a "3" would otherwise pull everything to its left along.
            "nxt tabular min-w-[2ch] shrink-0 text-right leading-snug",
            lg ? "text-3xl" : "text-xl",
            won === true ? undefined : "text-muted-foreground",
          )}
        >
          {score}
        </span>
      )}
    </span>
  );
}

function sideName(
  game: ScheduledGame,
  side: "a" | "b",
  ctx: ReturnType<typeof slotContext>,
  labelCtx: LabelCtx,
): string {
  return sideOf(game, side, ctx, labelCtx).text;
}

/**
 * Every game this team is in, seen from ITS side, chronological. Undecided
 * slots resolve exactly the way the schedule renders them, so a team whose
 * semifinal is still "winner of game 4" simply has no row for it yet.
 */
function teamGamesOf(
  team: Team,
  schedule: ScheduledGame[],
  ctx: ReturnType<typeof slotContext>,
  labelCtx: LabelCtx,
): TeamGameRow[] {
  return schedule
    .flatMap((g) => {
      const slots = gameSlots(g);
      const a = resolveSlot(slots.a, ctx);
      const b = resolveSlot(slots.b, ctx);
      const mine = a?.id === team.id ? "a" : b?.id === team.id ? "b" : null;
      if (mine === null) return [];
      const note = gameNoteOf(g);
      const r = g.result;
      return [
        {
          id: g.id,
          startAt: g.startAt,
          fieldName: fieldNameOf(labelCtx.fields, g.fieldId),
          opponent: sideName(g, mine === "a" ? "b" : "a", ctx, labelCtx),
          ...(note !== undefined ? { note } : {}),
          ...(r !== undefined
            ? {
                score: {
                  forUs: mine === "a" ? r.scoreA : r.scoreB,
                  against: mine === "a" ? r.scoreB : r.scoreA,
                },
              }
            : {}),
        },
      ];
    })
    .sort((x, y) => x.startAt.localeCompare(y.startAt));
}

/**
 * What a spectator standing at the fence actually wants: the next game on each
 * field, then the rest of the day grouped by start time. The old flat list of
 * every remaining game buried "what's on next" under twenty rows.
 */
function UpNext({
  games,
  fields,
  ctx,
  labelCtx,
  empty,
}: {
  games: ScheduledGame[];
  fields: Field[];
  ctx: ReturnType<typeof slotContext>;
  labelCtx: LabelCtx;
  empty: boolean;
}) {
  const t = useT();
  /**
   * Two shapes for the same games, the admin Schedule tab's reasoning in
   * miniature: a schedule is WHEN across WHERE, and the flat list makes the
   * reader undo the flattening in their head. The grid only exists where the
   * columns fit (`adm:`), so the state can sit at false on a phone forever;
   * with one field the toggle is not offered at all, because a one-column
   * grid IS the list.
   */
  const [byField, setByField] = useState(false);
  const { slug } = useTournament();
  if (games.length === 0) {
    return (
      <Section title={t("board.upNext")}>
        <Card className="p-4 text-sm text-muted-foreground">
          {empty ? t("board.scheduleSoon") : t("board.allPlayed")}
        </Card>
      </Section>
    );
  }

  // The soonest remaining game per field: the headline pair. A game whose
  // start is hours gone but never got a result (skipped, abandoned) must not
  // squat on the headline all day; prefer the first one that is still ahead-ish,
  // falling back to the stale one only when nothing else remains.
  const staleFloor = Date.now() - 2 * 60 * 60_000;
  const nextOn = (fieldId: FieldId): ScheduledGame | undefined => {
    const mine = games.filter((g) => g.fieldId === fieldId);
    return mine.find((g) => Date.parse(g.startAt) >= staleFloor) ?? mine[0];
  };
  const candidates = fields
    .map((f) => ({ field: f, game: nextOn(f.id) }))
    .filter((x): x is { field: Field; game: ScheduledGame } => x.game !== undefined);
  // ONE day only. A test match tonight on field 1 and Saturday's opener on
  // field 2 used to share the headline as "19:15" and "09:00" with nothing
  // saying they are a week apart - and the Saturday game then vanished from
  // Saturday's own list below. The headline is the soonest playing day; a
  // field with nothing that day simply has no card until the day is done.
  const nextDay = candidates.map((x) => x.game.startAt.slice(0, 10)).sort()[0];
  const nextPerField = candidates.filter((x) => x.game.startAt.slice(0, 10) === nextDay);
  const headline = new Set(nextPerField.map((x) => x.game.id));
  const later = games.filter((g) => !headline.has(g.id));

  // Bucketed by kickoff so a spectator reads "at 13:40 these two games", not
  // twenty separate rows, and split by DAY first, or a Sunday 9:00 game sorts
  // in after a Saturday 17:10 one under a heading that claims "later today".
  const byDay = new Map<string, Map<string, ScheduledGame[]>>();
  for (const g of later) {
    const day = g.startAt.slice(0, 10);
    const times = byDay.get(day) ?? new Map<string, ScheduledGame[]>();
    times.set(g.startAt, [...(times.get(g.startAt) ?? []), g]);
    byDay.set(day, times);
  }
  // LOCAL date, matching the zone-less startAt strings: toISOString is UTC
  // and mislabels the first hours after midnight east of Greenwich.
  const nowLocal = new Date();
  const today = `${nowLocal.getFullYear()}-${(nowLocal.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${nowLocal.getDate().toString().padStart(2, "0")}`;

  const list = (
    <div className="space-y-8">
      <Section title={t("board.upNext")}>
        <div className="grid gap-3 md:grid-cols-2">
          {nextPerField.map(({ field, game }) => (
            /* A floating pane (round 8), not the slanted stripe card it was: the
               headline of the schedule reads as the same kind of thing as the
               header pill above it. `ring-0` because the float draws its own
               border. */
            <Link
              key={field.id}
              to="/t/$slug/games/$gameId"
              params={{ slug, gameId: game.id }}
              className="block rounded-xl focus-visible:outline-2 focus-visible:outline-ring"
            >
              <Card className="board-float gap-2 p-4 ring-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="eyebrow text-muted-foreground">
                    {[
                      divisionTagOf(labelCtx.divisions, game.divisionId),
                      fieldNameOf(labelCtx.fields, game.fieldId),
                      // The day, always: a bare "09:00" on a Tuesday evening
                      // reads as tomorrow morning, and "today" is worth saying.
                      nextDay === today ? t("board.today") : dayHeading(nextDay ?? ""),
                    ]
                      .filter((x): x is string => x !== undefined)
                      .join(" · ")}
                  </span>
                  <span className="nxt tabular text-lg text-brand-ink">{timeOf(game.startAt)}</span>
                </div>
                <div className="flex flex-col gap-1.5">
                  <SideLine side={sideOf(game, "a", ctx, labelCtx)} size="md" />
                  <span className="eyebrow text-muted-foreground">{t("board.vsUpper")}</span>
                  <SideLine side={sideOf(game, "b", ctx, labelCtx)} size="md" />
                </div>
                <span className="text-sm font-semibold underline underline-offset-4">
                  {t("spectator.preview")}
                </span>
                {gameNoteOf(game) !== undefined && (
                  <div className="eyebrow text-muted-foreground">
                    {(gameNoteOf(game) ?? "").toUpperCase()}
                  </div>
                )}
              </Card>
            </Link>
          ))}
        </div>
      </Section>

      {[...byDay.entries()].map(([day, byTime]) => (
        <Section
          key={day}
          title={day === today ? t("board.laterToday") : dayHeading(day)}
          note={t("board.gamesCount", {
            count: [...byTime.values()].reduce((n, l) => n + l.length, 0),
          })}
        >
          <div className="space-y-3">
            {[...byTime.entries()].map(([time, list]) => (
              <div key={time} className="flex gap-3">
                {/* w-16, not w-12: `clockOf` zero-pads to 24h, so the widest time this
                    column can hold is `08:08`: 3.71em of NXT, or 59px at text-base.
                    w-12 was 48px and clipped it. */}
                <span className="nxt tabular w-16 shrink-0 pt-2 text-base text-muted-foreground">
                  {timeOf(time)}
                </span>
                <div className="min-w-0 flex-1 space-y-1.5">
                  {list.map((g) => (
                    /* The meta DROPS TO ITS OWN LINE rather than squeezing the
                       matchup, which is what every other list in this app
                       already does on a phone: the captain's schedule and the
                       Review tab put it in `RecordRow`'s `description` under
                       the title ("a matchup is two things of equal weight"),
                       and the admin's Schedule list gives it a line above the
                       stacked sides. This row was the one place that kept both
                       on one line with `shrink-0` on the WRONG half, so at
                       375px "SkyForce Women – Вовчиці" arrived as "SkyForce
                       Wo…" to leave room for "WOMEN · FIELD 2".

                       `flex-wrap` and a floor on the matchup instead of a
                       breakpoint branch: one markup, and the line breaks when
                       the names actually need it rather than when a media
                       query guesses they do. 12rem is roughly the shortest
                       matchup worth reading; below that the eye is decoding,
                       not scanning.

                       The matchup WRAPS rather than truncating, for
                       `RecordRow`'s stated reason: an undecided side is a
                       sentence, not a name, and "Програвший в 1-е місце vs
                       переможець 4/5" cut at the card edge says nothing at
                       all. Two of Sunday's rows are exactly that, and they
                       truncated even at 1280px. */
                    <ScheduleRow key={g.id} game={g} ctx={ctx} labelCtx={labelCtx} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>
      ))}
    </div>
  );

  return (
    <>
      {fields.length > 1 && (
        <div className="hidden justify-end adm:flex">
          {/* One slanted shell; the two pills inside stay flush and their
              labels upright - Button handles both via the host marker. */}
          <div
            data-slant-host=""
            className="-skew-x-6 flex items-center gap-1 rounded-lg border bg-card p-1"
          >
            <Button
              size="sm"
              variant={byField ? "ghost" : "secondary"}
              aria-pressed={!byField}
              onClick={() => setByField(false)}
            >
              {t("board.next.list")}
            </Button>
            <Button
              size="sm"
              variant={byField ? "secondary" : "ghost"}
              aria-pressed={byField}
              onClick={() => setByField(true)}
            >
              {t("board.next.byField")}
            </Button>
          </div>
        </div>
      )}
      {byField ? (
        <>
          {/* The list stays underneath on a phone: the grid needs columns the
              screen does not have, and a toggle flipped on a laptop must not
              strand the same URL's phone view on nothing. */}
          <div className="hidden adm:block">
            <NextByField
              games={games}
              fields={fields}
              ctx={ctx}
              labelCtx={labelCtx}
              today={today}
            />
          </div>
          <div className="adm:hidden">{list}</div>
        </>
      ) : (
        list
      )}
    </>
  );
}

/**
 * One game in the schedule list: the two sides stacked with their chips, the
 * round's own name above when the organiser gave one ("ФІНАЛ" is what a
 * spectator is looking for), the division and the field as tags on the right.
 * The sides stack because a matchup is two things of equal weight, and the
 * result rows and the headline cards already read this way.
 */
function ScheduleRow({
  game,
  ctx,
  labelCtx,
}: {
  game: ScheduledGame;
  ctx: ReturnType<typeof slotContext>;
  labelCtx: LabelCtx;
}) {
  const { slug } = useTournament();
  const t = useT();
  const note = gameNoteOf(game);
  const division = divisionTagOf(labelCtx.divisions, game.divisionId);
  const fieldName = fieldNameOf(labelCtx.fields, game.fieldId);
  return (
    <Link
      to="/t/$slug/games/$gameId"
      params={{ slug, gameId: game.id }}
      className="board-pane block rounded-xl border bg-card px-3 py-2 hover:bg-muted/60 focus-visible:outline-2 focus-visible:outline-ring"
    >
      {note !== undefined && (
        <span className="eyebrow mb-1.5 block text-muted-foreground">{note.toUpperCase()}</span>
      )}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <span className="flex min-w-0 flex-1 basis-48 flex-col gap-1">
          <SideLine side={sideOf(game, "a", ctx, labelCtx)} />
          <SideLine side={sideOf(game, "b", ctx, labelCtx)} />
        </span>
        <span className="flex shrink-0 items-center gap-1.5">
          {division !== undefined && <Tag tone="brand">{division}</Tag>}
          {fieldName !== undefined && <Tag>{fieldName}</Tag>}
        </span>
      </div>
      <span className="mt-2 block text-sm font-semibold underline underline-offset-4">
        {t("spectator.preview")}
      </span>
    </Link>
  );
}

/** The width contract the admin grid landed on: floored so six pitches scroll
 *  sideways instead of crushing, capped so two do not become 500px slabs. */
const NEXT_COL = "min-w-44 max-w-sm flex-1 basis-0";

/**
 * The Next view's wide shape: time down the left, fields across, one card per
 * game — the shape every printed tournament schedule has always had. Rows are
 * start-time buckets rather than the admin's proportional timeline: a
 * spectator asks "what starts when, where", not how long each block runs, and
 * the timeline's machinery earns its keep on the editing surface.
 */
function NextByField({
  games,
  fields,
  ctx,
  labelCtx,
  today,
}: {
  games: ScheduledGame[];
  fields: Field[];
  ctx: ReturnType<typeof slotContext>;
  labelCtx: LabelCtx;
  today: string;
}) {
  const t = useT();
  const byDay = new Map<string, ScheduledGame[]>();
  for (const g of games) {
    const day = g.startAt.slice(0, 10);
    byDay.set(day, [...(byDay.get(day) ?? []), g]);
  }

  return (
    <div className="space-y-8">
      {[...byDay.entries()].map(([day, dayGames]) => {
        // Only fields with a game THIS day: a column of empty cells for a
        // pitch that finished on Saturday says nothing on Sunday.
        const cols = fields.filter((f) => dayGames.some((g) => g.fieldId === f.id));
        const times = [...new Set(dayGames.map((g) => g.startAt))].sort();
        return (
          <Section
            key={day}
            title={day === today ? t("board.today") : dayHeading(day)}
            note={t("board.gamesCount", { count: dayGames.length })}
          >
            <div className="overflow-x-auto pb-1">
              <div className="min-w-max">
                <div className="mb-1 flex gap-2">
                  <div className="w-14 shrink-0" />
                  {cols.map((f) => (
                    <div key={f.id} className={cn(NEXT_COL, "px-1")}>
                      <span className="eyebrow block truncate text-muted-foreground">{f.name}</span>
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  {times.map((time) => (
                    <div key={time} className="flex gap-2">
                      <span className="nxt tabular w-14 shrink-0 pt-2 text-base text-muted-foreground">
                        {timeOf(time)}
                      </span>
                      {cols.map((f) => (
                        <div key={f.id} className={cn(NEXT_COL, "space-y-1.5")}>
                          {dayGames
                            .filter((g) => g.startAt === time && g.fieldId === f.id)
                            .map((g) => (
                              <ScheduleRow key={g.id} game={g} ctx={ctx} labelCtx={labelCtx} />
                            ))}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Section>
        );
      })}
    </div>
  );
}

// ---------- tournament leaders (from /api/stats snapshots) ----------

interface LeaderSection {
  key: string;
  /** Division name: user content. */
  title: string;
  rows: PlayerStatRow[];
  coverage: Coverage;
}

/** Top rows per table. Truncation is always stated, never silent. */
const LEADERS_SHOWN = 12;

function pct(rate: number | null): string {
  return rate === null ? "—" : `${Math.round(rate * 100)}%`;
}

/**
 * Per-division scoring leaders.
 *
 * Three sentences of context are load-bearing rather than decoration, and all
 * three are in the markup below:
 *
 *   · these are OFFENSIVE numbers: blocks and turnovers are deliberately not
 *     recorded (README), so the top row is not "best player at the tournament";
 *   · credit is optional, so each table states what share of its points carry a
 *     scorer at all. Without it the table quietly ranks scorekeepers;
 *   · "GP" is the games their TEAM played, because nothing here records who
 *     actually took the field.
 */
function Leaders({
  sections,
  minGames,
  perGame,
  sortKey,
  onSort,
  profiles,
}: {
  sections: LeaderSection[];
  minGames: number;
  /** Rank by rate instead of totals (Stats settings). The fairness case: on
   *  totals, a player whose team went out after pool play cannot catch a
   *  finalist however well they played per game. */
  perGame: boolean;
  sortKey: StatKey;
  onSort: (key: StatKey) => void;
  /** Rows with a public profile, by the row's id (a merged row's is its person's). */
  profiles: ReadonlyMap<string, RosterProfileLink>;
}) {
  const t = useT();
  return (
    <div className="space-y-4">
      <Note>{t("board.leadersCaveat")}</Note>

      {/* A real control, read one-handed at a pitch, so it keeps the 44px
          floor: unlike the header's jump chips, which are anchors rather than
          something anybody aims at mid-sentence. A 2×2 GRID on a phone: a
          wrapping flex row left the last chip stretched across its own line,
          which reads like a bug. */}
      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
        {STAT_KEYS.map((key) => (
          <Button
            key={key}
            // Solid ink, not pink: pink marks brand and featured things, and
            // "this is the column I am ranking by" is a selection. The outgoing
            // dark board had to use pink here because ink was invisible on it.
            variant={key === sortKey ? "default" : "outline"}
            aria-pressed={key === sortKey}
            className="text-sm font-semibold tracking-wide"
            onClick={() => onSort(key)}
            title={statLabel(key)}
          >
            {t(`board.sort.${key}`)}
          </Button>
        ))}
      </div>

      {sections.map((section) => {
        const ranked = rankPlayers(section.rows, {
          key: sortKey,
          ...(perGame ? { mode: "perGame" as const } : {}),
          minGames,
        });
        const shown = ranked.slice(0, LEADERS_SHOWN);
        return (
          <div key={section.key}>
            <SubHead note={t("board.coverage", { pct: pct(creditRate(section.coverage)) })}>
              {section.title}
            </SubHead>
            <Card className="gap-0 overflow-hidden p-0">
              <div
                className={cn(
                  "grid items-center border-b px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground",
                  LEADER_COLS,
                )}
              >
                <span />
                <span>{t("board.col.player")}</span>
                <span className="hidden adm:block">{t("board.col.team")}</span>
                <span className={cn("text-right", sortKey === "goals" && "text-foreground")}>
                  {t("board.col.goals")}
                </span>
                <span className={cn("text-right", sortKey === "assists" && "text-foreground")}>
                  {t("board.col.assists")}
                </span>
                <span className={cn("text-right", sortKey === "breaks" && "text-foreground")}>
                  {t("board.col.breaks")}
                </span>
                <span className="hidden text-right adm:block">{t("board.col.teamGames")}</span>
                {/* The chosen column PER GAME, always shown on a laptop: with
                    uneven game counts the rate is the honest comparison whether
                    or not the event ranks by it. Bold when it IS what ranks. */}
                <span className={cn("hidden text-right adm:block", perGame && "text-foreground")}>
                  {t("board.col.perGame")}
                </span>
              </div>
              {shown.map((r) => (
                <LeaderRow
                  key={r.playerId}
                  row={r}
                  sortKey={sortKey}
                  perGame={perGame}
                  profileId={profiles.get(r.playerId)?.accountId}
                />
              ))}
            </Card>
            {ranked.length > shown.length && (
              <Note className="mt-2">
                {t("board.topOf", { shown: shown.length, total: ranked.length })}
              </Note>
            )}
          </div>
        );
      })}

      <Note>
        {perGame && `${t("board.perGameNote")} `}
        {t("board.brkNote")}
        {minGames > 1 && ` ${t("board.rankedAfterGames", { count: minGames })}`}
      </Note>
    </div>
  );
}

/** Rank · player (team under it on a phone) · team · G · A · BRK · GP · per game. */
const LEADER_COLS =
  "grid-cols-[1.5rem_minmax(0,1fr)_2rem_2rem_2.5rem] gap-x-2 adm:grid-cols-[2.5rem_minmax(0,1fr)_7rem_3rem_3rem_3rem_3rem_4.5rem] adm:gap-x-3";

function LeaderRow({
  row,
  sortKey,
  perGame,
  profileId,
}: {
  row: RankedPlayerRow;
  sortKey: StatKey;
  perGame: boolean;
  /** The player's public profile, when they have one: the row opens it. */
  profileId: string | undefined;
}) {
  const breaks = row.breakGoals + row.breakAssists;
  const rate = statRate(row, sortKey);
  const cell = (key: StatKey): string =>
    cn(
      "tabular text-right text-sm",
      sortKey === key ? "font-semibold text-foreground" : "text-muted-foreground",
    );
  // A merged row names every squad the person played for, most-played
  // first, so "one player, two teams" is visible rather than implied.
  const teamLine = (
    <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
      {row.teamColor !== null && <ColorDot color={row.teamColor} className="size-2" />}
      <span className="truncate">{row.teamShorts.join(" + ")}</span>
    </span>
  );
  return (
    // A player with a public profile is a link, the team page roster's
    // convention (2026-09-15): the whole row, the name underlined.
    <RosterProfileRow
      profileId={profileId}
      className={cn(
        "grid items-center border-b px-3 py-2 last:border-b-0",
        LEADER_COLS,
        row.rank === 1 && "board-leader",
        // The wash is an image over the row, so the link's `hover:bg-accent`
        // would show nothing under it (GLASS.md, the washes).
        row.rank === 1 && profileId !== undefined && "transition-[filter] hover:brightness-110",
      )}
    >
      {/* Level players share a rank and the row says so: there is no
          tiebreak worth inventing between two people who scored eight. */}
      <span
        className={cn(
          "nxt tabular text-lg leading-none",
          row.rank !== null && row.rank <= 3 ? "text-foreground" : "text-muted-foreground",
        )}
      >
        {row.rank === null ? "–" : `${row.tied ? "=" : ""}${row.rank}`}
      </span>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="min-w-0 truncate text-sm font-medium">
          {row.number !== "" && (
            <span className="tabular text-muted-foreground">#{row.number} </span>
          )}
          <span className={cn(profileId !== undefined && "underline underline-offset-4")}>
            {row.name}
          </span>
        </span>
        <span className="adm:hidden">{teamLine}</span>
      </span>
      <span className="hidden adm:block">{teamLine}</span>
      <span className={cell("goals")}>{row.goals}</span>
      <span className={cell("assists")}>{row.assists > 0 ? row.assists : "—"}</span>
      <span className={cell("breaks")}>{breaks > 0 ? breaks : "—"}</span>
      <span className="tabular hidden text-right text-sm text-muted-foreground adm:block">
        {row.teamGames}
      </span>
      <span
        className={cn(
          "tabular hidden text-right text-sm adm:block",
          perGame ? "font-semibold text-foreground" : "text-muted-foreground",
        )}
      >
        {rate === null ? "—" : rate.toFixed(2).replace(/\.?0+$/, "")}
      </span>
    </RosterProfileRow>
  );
}

// ---------- spirit (published only when the tournament says so) ----------

/**
 * One division's spirit ranking, under that division's tables, in the same
 * shape as the standings above it: rank in the display face, the team's
 * coloured code, then games received and the average. Level teams share a
 * rank and the table SAYS so: WFDF publishes no tiebreak, so inventing a
 * separator here would be this app making up a result.
 */
function SpiritTable({ rows, minGames }: { rows: SpiritStandingRow[]; minGames: number }) {
  const t = useT();
  const cols =
    "grid-cols-[1.5rem_minmax(0,1fr)_2.5rem_3.5rem] gap-x-2 adm:grid-cols-[2.5rem_minmax(0,1fr)_3rem_4.5rem] adm:gap-x-3";
  return (
    <div className="space-y-2">
      <Card className="gap-0 overflow-hidden p-0">
        <div
          className={cn(
            "grid items-center border-b px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground",
            cols,
          )}
        >
          <span />
          <span>{t("board.col.team")}</span>
          <span className="text-right">{t("board.col.teamGames")}</span>
          <span className="text-right">{t("board.col.average")}</span>
        </div>
        {rows.map((r, i) => {
          return (
            <div
              key={r.team.id}
              className={cn(
                "grid items-center border-b px-3 py-2 last:border-b-0",
                cols,
                i === 0 && "board-leader",
              )}
            >
              <span
                className={cn(
                  "nxt tabular text-lg leading-none",
                  r.rank !== null && r.rank <= 3 ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {r.rank === null ? "–" : `${r.tied ? "=" : ""}${r.rank}`}
              </span>
              <span className="flex min-w-0 items-center gap-2">
                <TeamCode team={r.team} />
                <span className="min-w-0 truncate font-medium">{r.team.name}</span>
              </span>
              <span className="tabular text-right text-sm text-muted-foreground">{r.games}</span>
              <span className="tabular text-right text-sm font-semibold">
                {r.average.toFixed(2)}
              </span>
            </div>
          );
        })}
      </Card>
      {minGames > 1 && <Note>{t("board.rankedAfterScores", { count: minGames })}</Note>}
    </div>
  );
}

// ---------- a team's own page (board redesign, round 4) ----------

function byNumberThenName(a: Player, b: Player): number {
  const na = Number.parseInt(a.number, 10);
  const nb = Number.parseInt(b.number, 10);
  const aHas = Number.isFinite(na);
  const bHas = Number.isFinite(nb);
  if (aHas && bHas) return na - nb;
  if (aHas) return -1;
  if (bHas) return 1;
  return a.name.localeCompare(b.name);
}

type SubpageLink = { label: string; href: string; external?: boolean };

/**
 * The way from a team's public page into its workspace (`/t/<slug>/team/<id>`),
 * shown only to the people whose team it is (`useHeldTeams`). The default pill,
 * like the club profile's "Open club workspace": it is the one action on the
 * page that is this viewer's own. The board's shortcuts are
 * `TeamWorkspaceShortcuts`, in its section bar.
 */
function TeamWorkspaceButton({
  slug,
  team,
  className,
}: {
  slug: string;
  team: Team;
  className?: string;
}) {
  const t = useT();
  return (
    // `min-w-0 max-w-full` and a truncating label: a team name may be 40
    // characters, and a nowrap pill that long pushed a phone page sideways.
    <Button
      className={cn("min-w-0 max-w-full", className)}
      render={<Link to="/t/$slug/team/$club" params={{ slug, club: team.id }} />}
    >
      <span className="truncate">{t("board.team.yourTeam")}</span>
      <ChevronRightIcon aria-hidden="true" />
    </Button>
  );
}

/** Public detail pages keep the event visible as well as the global way out.
 * Team pages give their identity priority with a compact, named event link. */
function SubpageHeader({
  backHref,
  links,
  compact = false,
}: {
  backHref: string;
  links: SubpageLink[];
  compact?: boolean;
}) {
  const t = useT();
  const { meta, slug } = useTournament();
  return (
    <>
      <AppHeader pageLinks={links} />
      <div className="mx-auto w-full max-w-3xl px-4 pt-5 adm:max-w-6xl">
        {compact ? (
          <Link
            to="/t/$slug"
            params={{ slug }}
            className="inline-flex max-w-full items-center gap-2 py-2 text-sm font-medium text-muted-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
          >
            <ArrowLeftIcon aria-hidden="true" className="size-4 shrink-0" />
            <span className="min-w-0 break-words">{meta.name}</span>
          </Link>
        ) : (
          <PageContext
            title={meta.name}
            eyebrow={t("nav.tournament")}
            backHref={backHref}
            backLabel={t("board.team.back")}
            titleHref={tournamentHref(slug)}
            links={links}
          />
        )}
      </div>
    </>
  );
}

/**
 * A team's public page: `/t/<slug>/teams/<id>`. What the popup off a
 * standings row used to show, as a page with an address: the crest and the
 * name, the record, the roster with the players' tournament numbers, and
 * every game seen from this team's side, played ones first. Shares the
 * board bundle and game rows with the results tab; verified public profiles
 * and cached club history add portraits, links and other tournament entries.
 *
 * `teams/<id>`, plural, so it never collides with the credentialed hub at
 * `team/<club>`.
 */
export function TeamPublicPage() {
  useGlassSurface();
  const t = useT();
  const { id: tid, slug, meta, api } = useTournament();
  const params = useParams({ strict: false });
  const teamId = params.teamId ?? "";
  const bundle = useQuery(boardBundleQuery(tid)).data;
  const teams = bundle?.teams ?? [];
  const schedule = bundle?.schedule ?? [];
  const divisions = bundle?.divisions ?? [];
  const pools = bundle?.pools ?? [];
  const stats = bundle?.stats ?? [];
  const fields = bundle?.fields ?? [];
  const spirit = bundle?.spirit ?? EMPTY_SPIRIT;
  const persons: Person[] = bundle?.persons ?? [];
  const team = teams.find((x) => x.id === teamId);
  // Only the people whose team this is see the way into its workspace.
  const isMine = useHeldTeams(tid, teams).includes(teamId);
  const navigate = useNavigate();
  const openGame = (game: ScheduledGame): void => {
    void navigate({ to: "/t/$slug/games/$gameId", params: { slug, gameId: game.id } });
  };
  // Old teams share their club's id; newer squads carry an explicit clubId.
  // Verify either relationship so standalone teams never get a broken link.
  const clubs = useQuery({ ...clubsQuery(), enabled: team !== undefined }).data;
  const club = team !== undefined ? clubOfTeam(team, clubs ?? []) : undefined;
  const alsoQuery = useQuery({
    ...clubTournamentsQuery(club?.id ?? ""),
    enabled: club !== undefined,
  });
  // Club history includes every squad, not just this event lineup.
  const also = (alsoQuery.data ?? []).filter((row) => row.id !== tid && row.status !== "draft");
  // The captain and spirit captain are account assignments (2026-09-15); the
  // roster's own `isCaptain` is the library's old display flag and tags only
  // an event that has no assignments at all. Nothing is tagged until the
  // answer arrives, so a spirit captain never flashes as a captain first.
  const rolesQuery = useQuery({
    queryKey: ["team-roles", tid, teamId],
    queryFn: () => api.teamRoles(teamId),
    enabled: team !== undefined,
    staleTime: 60_000,
  });
  const roles = rolesQuery.data;
  const assigned = roles !== undefined && roles.captain.length + roles.spirit.length > 0;
  const legacyFlags = rolesQuery.isError || (roles !== undefined && !assigned);
  const profileQuery = useQuery({ ...tournamentProfilesQuery(tid), enabled: team !== undefined });
  const profiles = new Map((profileQuery.data ?? []).map((p) => [p.playerId, p]));

  useEffect(() => {
    if (team !== undefined) document.title = `${team.name} · ${meta.short ?? meta.name}`;
  }, [team, meta]);

  const ctx = slotContext(teams, schedule);
  const labelCtx = { teams, divisions, schedule, pools, fields };
  const identity = identityFrom(persons);
  const credited = withCredit(playerStats({ stats, teams, schedule }, identity));

  const back = <SubpageHeader backHref={tournamentHref(slug)} links={[]} compact />;

  if (bundle === undefined) {
    return (
      <div className="min-h-dvh pb-12">
        <div className="board-ground" aria-hidden="true" />
        {back}
      </div>
    );
  }
  if (team === undefined) {
    return (
      <div className="min-h-dvh pb-12">
        <div className="board-ground" aria-hidden="true" />
        {back}
        <main className="mx-auto w-full max-w-3xl px-4 pt-6 adm:max-w-6xl">
          <Card className="p-4 text-sm text-muted-foreground">{t("board.team.missing")}</Card>
        </main>
      </div>
    );
  }

  const games = teamGamesOf(team, schedule, ctx, labelCtx);
  const played = games.filter((g) => g.score !== undefined);
  const wins = played.filter(
    (g) => g.score !== undefined && g.score.forUs > g.score.against,
  ).length;
  const losses = played.filter(
    (g) => g.score !== undefined && g.score.forUs < g.score.against,
  ).length;
  const draws = played.length - wins - losses;
  const diff = played.reduce(
    (n, g) => n + (g.score === undefined ? 0 : g.score.forUs - g.score.against),
    0,
  );
  const statRows = credited.filter((r) => r.teamId === team.id);
  const rowsById = new Map(statRows.map((r) => [r.playerId, r]));
  const claimed = new Set<string>();
  const roster: RosterLine[] = [...(team.roster ?? [])].sort(byNumberThenName).map((p) => {
    const row = rowsById.get(identity.idOf(p.id));
    if (row !== undefined) claimed.add(row.playerId);
    return {
      key: p.id,
      number: p.number,
      name: p.name,
      captain: assigned ? roles.captain.includes(p.id) : legacyFlags && p.isCaptain,
      spirit: assigned && roles.spirit.includes(p.id),
      pickup: p.pickup === true,
      row,
    };
  });
  // Anyone credited in a game but since removed from the roster still
  // appears, after it: a number on the Leaders tab must never be unfindable.
  const departed: RosterLine[] = statRows
    .filter((r) => !claimed.has(r.playerId))
    .map((r) => ({
      key: r.playerId,
      number: r.number,
      name: r.name,
      captain: false,
      spirit: false,
      pickup: false,
      row: r,
    }));
  const players = [...roster, ...departed];
  const divisionName =
    team.divisionId !== undefined ? divisionNameOf(divisions, team.divisionId) : undefined;
  const poolName = pools.find((p) => p.id === team.poolId)?.name;
  const spiritGames =
    spirit.visibility === "games" || spirit.visibility === "full" ? spirit.games : [];
  const finishedGames = [...schedule]
    .filter((g) => g.result !== undefined && played.some((p) => p.id === g.id))
    .reverse();
  const upcomingGames = schedule.filter(
    (g) => g.result === undefined && games.some((x) => x.id === g.id),
  );

  return (
    <div className="min-h-dvh pb-12">
      <div className="board-ground" aria-hidden="true" />
      {back}
      <main className="board-enter animate-in fade-in-0 slide-in-from-bottom-2 mx-auto w-full max-w-3xl space-y-6 px-4 pt-4 duration-300 adm:max-w-6xl">
        <header className="space-y-3">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
            <ClubIdentity
              name={team.name}
              logo={team.logo}
              logoDark={team.logoDark}
              short={team.short}
              color={team.color}
              large
            />
            {(isMine || club !== undefined) && (
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 sm:shrink-0">
                {isMine && <TeamWorkspaceButton slug={slug} team={team} />}
                {club !== undefined && (
                  <a
                    href={`/teams/${encodeURIComponent(club.slug)}`}
                    className="inline-flex items-center gap-1 py-2 text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
                  >
                    {t("board.team.viewClub")}
                    <ArrowUpRightIcon aria-hidden="true" className="size-3.5" />
                  </a>
                )}
              </div>
            )}
          </div>
          {played.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
              <span>{[divisionName, poolName].filter(Boolean).join(" · ")}</span>
              <span className="inline-flex items-center gap-2">
                <CalendarDaysIcon aria-hidden="true" className="size-4 shrink-0" />
                {dateRangeOf(meta.startDate, meta.endDate)}
              </span>
              {(meta.city || meta.venue) && (
                <span className="inline-flex items-center gap-2">
                  <MapPinIcon aria-hidden="true" className="size-4 shrink-0" />
                  {[meta.city, meta.venue].filter(Boolean).join(" · ")}
                </span>
              )}
            </div>
          )}
        </header>

        <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile label={t("directory.players")} value={String(roster.length)} />
          {played.length > 0 ? (
            <>
              <StatTile label={t("board.team.wins")} value={String(wins)} />
              <StatTile
                label={draws > 0 ? t("board.team.lossesDraws") : t("board.team.losses")}
                value={draws > 0 ? `${losses} · ${draws}` : String(losses)}
              />
              <StatTile
                label={t("board.col.diff")}
                value={diff > 0 ? `+${diff}` : String(diff)}
                tone={diff > 0 ? "go" : "plain"}
              />
            </>
          ) : (
            <>
              <StatTile
                label={t("tournamentPlayers.division")}
                value={divisionName ?? "—"}
                detail={poolName}
                text
              />
              <StatTile
                label={t("board.info.whenWhere")}
                value={dateRangeOf(meta.startDate, meta.endDate)}
                text
              />
              <StatTile
                label={meta.city ? t("directory.city") : t("board.team.location")}
                value={meta.city || meta.venue || "—"}
                detail={meta.city ? meta.venue : undefined}
                text
              />
            </>
          )}
        </section>

        <Suspense fallback={null}>
          <TeamNextMatch team={team} ctx={ctx} labelCtx={labelCtx} />
        </Suspense>

        {played.length > 0 && (
          <Suspense fallback={<p role="status">{t("common.loading")}</p>}>
            <TeamProgression team={team} ctx={ctx} labelCtx={labelCtx} />
          </Suspense>
        )}

        <Section
          title={t("board.roster")}
          note={players.length > 0 ? String(players.length) : undefined}
        >
          {players.length === 0 ? (
            <Card className="p-4 text-sm text-muted-foreground">{t("board.rosterEmpty")}</Card>
          ) : (
            <Suspense fallback={<p role="status">{t("common.loading")}</p>}>
              <TeamRoster players={players} profiles={profiles} />
            </Suspense>
          )}
        </Section>

        {(finishedGames.length > 0 || upcomingGames.length > 0) && (
          <Section
            title={t("board.teamGames")}
            note={t("board.gamesCount", { count: games.length })}
          >
            <div className="grid gap-1.5 adm:grid-cols-[repeat(auto-fit,minmax(24rem,1fr))]">
              {finishedGames.map((g) => (
                <ResultRow
                  key={g.id}
                  game={g}
                  spirit={spiritGames.find((x) => x.gameId === g.id)}
                  ctx={ctx}
                  labelCtx={labelCtx}
                  onOpen={openGame}
                />
              ))}
              {upcomingGames.map((g) => (
                <ScheduleRow key={g.id} game={g} ctx={ctx} labelCtx={labelCtx} />
              ))}
            </div>
          </Section>
        )}

        {club !== undefined && (also.length > 0 || alsoQuery.isError) && (
          <Section
            title={t("board.team.also")}
            note={also.length > 0 ? t("board.tournamentsCount", { count: also.length }) : undefined}
          >
            {alsoQuery.isError && (
              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                <p>{t("board.loadFailed")}</p>
                <Button variant="outline" onClick={() => void alsoQuery.refetch()}>
                  {t("common.retry")}
                </Button>
              </div>
            )}
            {also.length > 0 && (
              <Suspense fallback={<p role="status">{t("common.loading")}</p>}>
                <RelatedClubHistory events={also} clubName={club.name} />
              </Suspense>
            )}
          </Section>
        )}
      </main>
    </div>
  );
}

/** A number the page leads with: the label small above, the figure in the
 *  display face. */
function StatTile({
  label,
  value,
  tone = "plain",
  text = false,
  detail,
}: {
  label: string;
  value: string;
  tone?: "plain" | "go";
  text?: boolean;
  detail?: string | undefined;
}) {
  return (
    <Card className="gap-1.5 p-4">
      <span className="eyebrow text-muted-foreground">{label}</span>
      <span
        className={cn(
          text
            ? "text-lg font-semibold leading-tight text-pretty break-words"
            : "nxt tabular text-3xl leading-none",
          tone === "go" && "text-go-ink",
        )}
      >
        {value}
      </span>
      {detail && <span className="text-xs text-muted-foreground break-words">{detail}</span>}
    </Card>
  );
}

// ---------- a game's own page (board redesign, round 5) ----------

/**
 * One game: `/t/<slug>/games/<id>`. The two sides with their chips and the
 * score in the display face, the day and place as tags, then the
 * point-by-point ledger - each point on its scoring team's side with the
 * running score between them, so a run of breaks reads as a column of ink
 * on one side - the published spirit scores, and the way out to the result
 * card. A game not yet played shows its sides and says so.
 *
 * Player names come from the SNAPSHOT rosters inside `GameStats`, not the
 * live ones: the snapshot records who actually played, and a roster edited
 * after the game must not rewrite its story.
 */
export function GamePublicPage() {
  useGlassSurface();
  const t = useT();
  const { id: tid, slug, meta, api } = useTournament();
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const gameId = params.gameId ?? "";
  const bundle = useQuery(boardBundleQuery(tid)).data;
  const teams = bundle?.teams ?? [];
  const schedule = bundle?.schedule ?? [];
  const divisions = bundle?.divisions ?? [];
  const pools = bundle?.pools ?? [];
  const stats = bundle?.stats ?? [];
  const fields = bundle?.fields ?? [];
  const spirit = bundle?.spirit ?? EMPTY_SPIRIT;
  const game = schedule.find((g) => g.id === gameId);
  // The scorers' names open their profiles: asked only once there is a result.
  const profiles = useQuery({
    ...tournamentProfilesQuery(tid),
    enabled: game?.result !== undefined,
  }).data;
  const ctx = slotContext(teams, schedule);
  const labelCtx = { teams, divisions, schedule, pools, fields };
  const a = game === undefined ? null : sideOf(game, "a", ctx, labelCtx);
  const b = game === undefined ? null : sideOf(game, "b", ctx, labelCtx);

  useEffect(() => {
    if (a !== null && b !== null) {
      document.title = `${a.text} – ${b.text} · ${meta.short ?? meta.name}`;
    }
  }, [a, b, meta]);

  // The result card is a way OUT, so it lives with the other ways out: in
  // the context row on a laptop, behind Menu on a phone. Only a played game has one.
  const back = (
    <SubpageHeader
      backHref={tournamentHref(slug, game?.result !== undefined ? "#results" : "#next")}
      links={
        game?.result !== undefined
          ? [{ label: t("board.resultCard"), href: api.paths.card(game.id), external: true }]
          : []
      }
    />
  );

  if (bundle === undefined) {
    return (
      <div className="min-h-dvh pb-12">
        <div className="board-ground" aria-hidden="true" />
        {back}
      </div>
    );
  }
  if (game === undefined || a === null || b === null) {
    return (
      <div className="min-h-dvh pb-12">
        <div className="board-ground" aria-hidden="true" />
        {back}
        <main className="mx-auto w-full max-w-3xl px-4 pt-6 adm:max-w-6xl">
          <Card className="p-4 text-sm text-muted-foreground">{t("board.noGame")}</Card>
        </main>
      </div>
    );
  }

  const result = game.result;
  const snap = stats.find((s) => s.scheduleGameId === game.id);
  const points = snap?.points ?? [];
  const spiritGame =
    spirit.visibility === "games" || spirit.visibility === "full"
      ? spirit.games.find((x) => x.gameId === game.id)
      : undefined;
  const spiritOf = (team: Team | null, giver: BoardSide): SpiritChip | undefined =>
    spiritChipOf(spiritGame, team, giver);
  const division = divisionTagOf(divisions, game.divisionId);
  const fieldName = fieldNameOf(fields, game.fieldId);
  const note = gameNoteOf(game);
  const openTeam = (team: Team | null): void => {
    if (team === null) return;
    void navigate({ to: "/t/$slug/teams/$teamId", params: { slug, teamId: team.id } });
  };

  return (
    <div className="min-h-dvh pb-12">
      <div className="board-ground" aria-hidden="true" />
      {back}
      <main className="board-enter animate-in fade-in-0 slide-in-from-bottom-2 mx-auto w-full max-w-3xl space-y-8 px-4 pt-6 duration-300 adm:max-w-6xl">
        <section className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="tabular text-sm font-medium text-muted-foreground">
              {dayHeading(game.startAt.slice(0, 10))} · {timeOf(game.startAt)}
            </span>
            {division !== undefined && <Tag tone="brand">{division}</Tag>}
            {fieldName !== undefined && <Tag>{fieldName}</Tag>}
            {note !== undefined && <Tag>{note}</Tag>}
          </div>
          <Card className="gap-0 overflow-hidden p-0">
            <GameSide
              side={a}
              score={result?.scoreA}
              won={result !== undefined && result.scoreA >= result.scoreB}
              spirit={spiritOf(a.team, b)}
              onOpen={() => openTeam(a.team)}
            />
            <GameSide
              side={b}
              score={result?.scoreB}
              won={result !== undefined && result.scoreB >= result.scoreA}
              spirit={spiritOf(b.team, a)}
              onOpen={() => openTeam(b.team)}
            />
          </Card>
          {result === undefined && (
            <p className="text-sm text-muted-foreground">{t("board.game.notPlayed")}</p>
          )}
        </section>

        {result === undefined && (
          <Suspense fallback={null}>
            <MatchPrediction gameId={game.id} />
          </Suspense>
        )}

        {result === undefined && (
          <Suspense fallback={<p>{t("common.loading")}</p>}>
            <MatchPreview game={game} ctx={ctx} labelCtx={labelCtx} />
          </Suspense>
        )}

        {result !== undefined && snap !== undefined && (
          <Section title={t("board.story.title")}>
            <GameStory
              snap={snap}
              a={{ short: snap.teamA.short, color: a.team?.color ?? "#ff6fa5" }}
              b={{ short: snap.teamB.short, color: b.team?.color ?? "#60a5fa" }}
              profiles={new Map((profiles ?? []).map((p) => [p.playerId, p.accountId]))}
            />
          </Section>
        )}

        {result !== undefined && (
          <Section title={t("board.game.pointByPoint")}>
            {points.length === 0 ? (
              <Card className="p-4 text-sm text-muted-foreground">{t("board.noPoints")}</Card>
            ) : (
              <Card className="gap-0 overflow-hidden p-0">
                <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-x-3 border-b px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <span className="truncate text-right">{snap?.teamA.short ?? a.text}</span>
                  <span />
                  <span className="truncate">{snap?.teamB.short ?? b.text}</span>
                </div>
                <div className="px-3 py-1">
                  {points.map((p) => (
                    <PointLedgerRow
                      key={`${p.at}-${p.scoreA}-${p.scoreB}`}
                      point={p}
                      stats={snap}
                    />
                  ))}
                </div>
              </Card>
            )}
          </Section>
        )}
      </main>
    </div>
  );
}

/** One side of the game's hero: the chip and the name (a link to the team's
 *  page when the side is a team), the spirit it received, the score. */
function GameSide({
  side,
  score,
  won,
  spirit,
  onOpen,
}: {
  side: BoardSide;
  score: number | undefined;
  won: boolean;
  spirit: SpiritChip | undefined;
  onOpen: () => void;
}) {
  const t = useT();
  return (
    <div className="flex items-center gap-3 border-b px-4 py-3 last:border-b-0 adm:gap-4 adm:px-5 adm:py-4">
      <button
        type="button"
        onClick={onOpen}
        disabled={side.team === null}
        className="flex min-w-0 flex-1 items-center gap-3 text-left disabled:cursor-default"
      >
        <SideLine side={side} size="md" />
        {side.team !== null && (
          <ChevronRightIcon
            aria-hidden="true"
            className="size-4 shrink-0 text-muted-foreground/60"
          />
        )}
      </button>
      {spirit !== undefined && (
        <span
          className="tabular inline-flex shrink-0 whitespace-nowrap rounded-md border px-1.5 py-0.5 text-[11px] font-semibold leading-none text-muted-foreground"
          title={t("board.spiritFromHint", { team: spirit.from })}
        >
          {t("board.spiritFrom", { n: spirit.total, team: spirit.from })}
        </span>
      )}
      {score !== undefined && (
        <span
          className={cn(
            "nxt tabular min-w-[2ch] shrink-0 text-right text-4xl leading-none adm:text-6xl",
            won ? undefined : "text-muted-foreground",
          )}
        >
          {score}
        </span>
      )}
    </div>
  );
}

/**
 * One point of the ledger: its side carries the credit, the middle carries
 * the running score with the scoring team's number leading in ink.
 */
function PointLedgerRow({ point, stats }: { point: PointEvent; stats: GameStats | undefined }) {
  const t = useT();
  const scoringA = point.team === "A";
  const roster = (scoringA ? stats?.teamA.roster : stats?.teamB.roster) ?? [];
  const scorer = snapshotNameOf(roster, point.scorerId);
  const assist = snapshotNameOf(roster, point.assistId);
  const credit = (
    <span className={cn("min-w-0 text-xs", scoringA ? "text-right" : "text-left")}>
      {/* Wraps rather than truncates: a phone gives each side ~40% of the
          row, and a scorer cut to "#13 Yekateryna Kho…" credits nobody. */}
      {scorer !== null && <span className="block font-medium text-pretty">{scorer}</span>}
      {assist !== null && (
        <span className="block text-muted-foreground text-pretty">
          {t("board.assistBy", { name: assist })}
        </span>
      )}
      {isBreak(point) && (
        <span className="eyebrow block text-brand-ink">{t("board.breakPoint")}</span>
      )}
    </span>
  );
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-x-3 border-b border-dashed py-1.5 last:border-b-0">
      {scoringA ? credit : <span />}
      <span className="nxt tabular shrink-0 text-base">
        <span className={cn(scoringA ? undefined : "text-muted-foreground")}>{point.scoreA}</span>
        <span className="px-0.5 text-muted-foreground">-</span>
        <span className={cn(scoringA ? "text-muted-foreground" : undefined)}>{point.scoreB}</span>
      </span>
      {scoringA ? <span /> : credit}
    </div>
  );
}

function snapshotNameOf(roster: Player[], id: string | null): string | null {
  if (id === null) return null;
  const p = roster.find((x) => x.id === id);
  return p === undefined ? null : playerLabel(p);
}
